import { useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

import { kacheloptionen } from '../../lib/kartenquelle'
import { nadelbild } from '../map/nadelbild'
import { useBuchquelle } from './buchquelle'

/**
 * Wie weit die kleine Karte herauszoomt.
 *
 * Aus dem Betrieb: „damit man sieht wo das ist". Zwölf zeigt den Ort und sein
 * Umland — bei sechzehn sähe man die Straße und wüsste immer noch nicht, in
 * welchem Land man ist; bei acht das halbe Land und nichts vom Ort. Die
 * Einordnung ins Ganze leistet die Übersichtskarte vorn.
 */
const ZOOM = 12

/**
 * Ein kleiner Kartenausschnitt neben einem Punkt (#14).
 *
 * **Eine Nadel und sonst nichts.** Aus dem Betrieb wörtlich: „mit nur dem
 * einen Marker". Die Spots des Punktes stehen als Zeilen darunter und nicht
 * als zweite Nadeln — dieselbe Entscheidung wie auf der großen Karte, wo aus
 * einem Reisetag sonst ein Stadtplan würde.
 *
 * **Ohne Lizenzvermerk am Bild, und das ist keine Nachlässigkeit.** Er steht
 * einmal unter der Überschrift des Abschnitts und gilt dort für alle
 * Ausschnitte; fünfzehn Mal derselbe Satz in Sechs-Punkt-Schrift wäre
 * Papierrauschen und keine Nennung. Auf der Übersichtskarte steht er weiter am
 * Bild, wo Leaflet ihn hinsetzt.
 *
 * **Und sie sagt Bescheid, wenn sie fertig ist.** Ohne das druckt man graue
 * Kästen: der Druck wartet nicht auf Bilder aus dem Netz. Der Knopf im Kopf
 * bleibt zu, bis jede einzelne Karte gemeldet hat.
 */
export function Punktkarte({
  punktId,
  lat,
  lon,
  nummer,
  titel,
  onFertig,
}: {
  /** Set as `data-punkt` on the box: that is how the export finds the still image for the point. */
  punktId: number
  lat: number
  lon: number
  nummer: number
  titel: string
  onFertig: () => void
}) {
  const kasten = useRef<HTMLDivElement | null>(null)
  const quelle = useBuchquelle()

  /*
   * **Die Meldung liegt in einem Ref, nicht in den Abhängigkeiten.** Derselbe
   * Grund wie bei der Übersichtskarte: ein Rückruf ist bei jedem Zeichnen neu,
   * der Effekt liefe also erneut, räumte die Karte ab und stiege an der Sperre
   * wieder aus. Übrig bliebe ein leerer Kasten, der nie „fertig" sagt — und
   * der Druckknopf bliebe für immer zu.
   */
  const melder = useRef(onFertig)
  melder.current = onFertig
  const gebautFuer = useRef<string | null>(null)

  const schluessel = `${quelle.tile_url}|${lat},${lon}`

  useEffect(() => {
    if (kasten.current === null) return
    if (gebautFuer.current === schluessel) return
    gebautFuer.current = schluessel

    const karte = L.map(kasten.current, {
      zoomControl: false,
      // Der Vermerk steht einmal am Abschnitt, nicht an jedem Ausschnitt.
      attributionControl: false,
      // Nichts davon wird gebraucht: gedruckt wird ein Standbild.
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      touchZoom: false,
      keyboard: false,
    })

    // `crossOrigin`, so that the still image for the book may copy the tiles (`mapImage.ts`).
    const ebene = L.tileLayer(quelle.tile_url, { ...kacheloptionen(quelle), crossOrigin: 'anonymous' })

    /*
     * `load` feuert, wenn alle sichtbaren Kacheln da sind. Die Uhr darüber ist
     * die Sicherung: antwortet der Kartenserver nicht, soll der Druck nicht
     * für immer gesperrt bleiben — bei fünfzehn Ausschnitten genügte ein
     * einziger, der schweigt.
     */
    let gemeldet = false
    const melde = (): void => {
      if (gemeldet) return
      gemeldet = true
      melder.current()
    }
    ebene.on('load', melde)
    const uhr = window.setTimeout(melde, 12000)
    ebene.addTo(karte)

    karte.setView([lat, lon], Math.min(ZOOM, quelle.max_zoom))

    L.marker([lat, lon], {
      icon: L.divIcon({
        // Leer heißt leer: so lässt Leaflet sein `leaflet-div-icon` weg und
        // damit den weißen Kasten, den es sonst um jedes `divIcon` malt.
        className: '',
        html: nadelbild('buch__nadel', { nummer }),
        iconSize: [22, 30],
        // Die Spitze zeigt auf den Ort, nicht die Mitte des Bildes.
        iconAnchor: [11, 30],
      }),
      title: titel,
      alt: titel,
      keyboard: false,
    }).addTo(karte)

    return () => {
      window.clearTimeout(uhr)
      karte.remove()
      gebautFuer.current = null
    }
  }, [schluessel])

  return <div className="buch__punktkarte" ref={kasten} data-punkt={punktId} />
}
